import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PenLine, Trash2, Upload, Lock } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

export default function AutographPanel() {
  const [pros, setPros] = useState([]);
  const [cards, setCards] = useState([]);
  const [name, setName] = useState("");
  const [photo, setPhoto] = useState(null);
  const [cardIds, setCardIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanPrice, setScanPrice] = useState(5);
  const [savingPrice, setSavingPrice] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/autograph/pros").then(({ data }) => setPros(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/vault/cards").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/admin/autograph/config").then(({ data }) => setScanPrice(data.rescan_price)).catch(() => {});
  }, []);

  const savePrice = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.put("/admin/autograph/config", { rescan_price: Number(scanPrice) || 0 });
      setScanPrice(data.rescan_price);
      toast.success(`Verification scan price set — first scan free, then ${data.rescan_price} BLC`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPrice(false); }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const addPro = async () => {
    setBusy(true);
    try {
      await api.post("/admin/autograph/pros", { name, photo, card_ids: cardIds });
      toast.success(`${name} registered — verification reference locked`);
      setName(""); setPhoto(null); setCardIds([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removePro = async (p) => {
    try { await api.delete(`/admin/autograph/pros/${p.id}`); toast.success(`${p.name} removed`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateCards = async (p, ids) => {
    setPros((l) => l.map((x) => (x.id === p.id ? { ...x, card_ids: ids } : x)));
    try { await api.put(`/admin/autograph/pros/${p.id}/cards`, { card_ids: ids }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const picker = (selected, onToggle, tid) => (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid={tid}>
      {cards.map((c) => {
        const on = selected.includes(c.id);
        return (
          <button key={c.id} onClick={() => onToggle(on ? selected.filter((x) => x !== c.id) : [...selected, c.id])}
            title={c.subject_name} data-testid={`${tid}-${c.id}`}
            className={`shrink-0 w-12 border-2 transition-colors ${on ? "border-yellow-400" : "border-pb-border opacity-50 hover:opacity-90"}`}>
            <img src={c.url} alt={c.subject_name} className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
              title="Tap to zoom" onClick={() => zoomCard(c.url, c.subject_name)} />
          </button>
        );
      })}
      {cards.length === 0 && <span className="text-[10px] text-zinc-600">No cards minted yet.</span>}
    </div>
  );

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="autograph-panel">
      <div className="flex items-center gap-2 mb-1"><PenLine className="w-4 h-4 text-yellow-400" /><span className="font-display font-bold uppercase">Verified Fan Signed Cards</span></div>
      <p className="text-xs text-zinc-500 mb-4">
        Register an athlete with ONE locked reference photo and pick which cards they're allowed to sign. Fans verify the
        athlete in person — a verified match opens a 5-minute signing box, and the signature gets baked into their copy
        with every step timestamped in the registry. Each card can only ever be signed once.
      </p>

      <div className="border border-pb-border bg-pb-elevated/40 p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="autograph-pricing">
        <div>
          <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Verification scan price (BLC)</label>
          <input data-testid="autograph-scan-price-input" type="number" min={0} step="0.5" value={scanPrice}
            onChange={(e) => setScanPrice(e.target.value)}
            className="w-32 bg-pb-elevated border border-pb-border px-2.5 py-2 text-xs text-white font-mono focus:outline-none focus:border-yellow-400" />
        </div>
        <button data-testid="autograph-scan-price-save" onClick={savePrice} disabled={savingPrice}
          className="flex items-center gap-1.5 bg-yellow-400 text-black font-black uppercase tracking-widest px-4 py-2.5 text-[10px] hover:bg-yellow-300 disabled:opacity-40">
          {savingPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />} Save Price
        </button>
        <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Every fan's FIRST verification scan is free — this price applies to every scan after that.</span>
      </div>

      <div className="border border-yellow-500/30 bg-yellow-500/5 p-3 mb-4 space-y-2.5" data-testid="autograph-add-form">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Pro name</label>
            <input data-testid="pro-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
              placeholder="e.g. Ben Johns"
              className="w-full bg-pb-elevated border border-pb-border px-2.5 py-2 text-xs text-white focus:outline-none focus:border-yellow-400" />
          </div>
          <div>
            <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Locked reference photo</label>
            <button data-testid="pro-photo-btn" onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 border border-pb-border px-3 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-300 hover:border-yellow-400 hover:text-yellow-300">
              <Upload className="w-3 h-3" /> {photo ? "Photo ready ✓" : "Upload photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="pro-photo-input" />
          </div>
          {photo && <img src={photo} alt="ref" className="w-10 h-10 object-cover border border-yellow-400" data-testid="pro-photo-preview" />}
          <button data-testid="pro-add-btn" onClick={addPro} disabled={!name.trim() || !photo || busy}
            className="flex items-center gap-1.5 bg-yellow-400 text-black font-black uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-yellow-300 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Register & Lock
          </button>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Cards this pro can sign ({cardIds.length} picked)</div>
          {picker(cardIds, setCardIds, "pro-new-cards")}
        </div>
      </div>

      <div className="divide-y divide-pb-border/40">
        {pros.map((p) => (
          <div key={p.id} className="py-3 space-y-2" data-testid={`pro-row-${p.id}`}>
            <div className="flex items-center gap-3">
              <img src={p.photo_url} alt={p.name} className="w-10 h-10 object-cover border border-yellow-500/60" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">{p.name}</div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">
                  <Lock className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />Locked {String(p.locked_at || "").slice(0, 10)} · {p.signatures} signature{p.signatures === 1 ? "" : "s"} baked
                </div>
              </div>
              <button data-testid={`pro-delete-${p.id}`} onClick={() => removePro(p)}
                className="p-2 border border-pb-border text-zinc-500 hover:border-red-500 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div>
              <div className="text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Signable cards ({p.card_ids.length}) — tap to toggle</div>
              {picker(p.card_ids, (ids) => updateCards(p, ids), `pro-cards-${p.id}`)}
            </div>
          </div>
        ))}
        {pros.length === 0 && <div className="py-4 text-xs text-zinc-600">No verified pros yet — register your first above.</div>}
      </div>
    </div>
  );
}
